import { Plugin, User } from '@segment/analytics-next'
import { sha256 } from 'js-sha256'
import { type Kochava } from 'kochava'
import { Identify, Track } from 'segmentio-facade'
import { namedLogger } from '../../../utils'

const logger = namedLogger('Kochava Plugin')

let kochavaInstance: Kochava | null = null

async function setupKochavaInstance(appGuid: string) {
  const kochava = kochavaInstance ?? (await import('kochava')).Kochava.create()
  kochava.disableAutoPage(true)
  kochava.useCookies(true)
  kochava.startWithAppGuid(appGuid)
  return kochava
}

export async function getKochavaInstance(appGuid: string | undefined) {
  if (!kochavaInstance && appGuid) {
    kochavaInstance = await setupKochavaInstance(appGuid)
  }
  if (!kochavaInstance) {
    throw new Error('Kochava instance not found, please check your app GUID')
  }
  return kochavaInstance
}

function KochavaEnrichmentPlugin({ appGuid }: { appGuid: string | undefined }): Plugin {
  let user: User | null = null
  if (!appGuid) {
    throw new Error('Kochava app GUID is not set')
  }
  return {
    name: `kochava-enrichment-${appGuid}`,
    version: '0.1.0',
    type: 'enrichment',
    // use the `load` hook to bootstrap your plugin
    // The load hook will receive a context object as its first argument
    // followed by a reference to the analytics.js instance from the page
    load: async (_ctx, ajs) => {
      user = ajs.user()
    },
    // Used to signal that a plugin has been property loaded
    isLoaded: () => user != null,
    ready: async () => true,
    page: async (ctx) => {
      const kochava = await getKochavaInstance(appGuid)
      const kochava_device_id = kochava.getDeviceId()
      ctx.updateEvent('properties.kochava_device_id', kochava_device_id)
      user?.traits({
        kochava_device_id,
      })
      return ctx
    },
    track: async (ctx) => {
      const kochava = await getKochavaInstance(appGuid)
      const kochava_device_id = kochava.getDeviceId()
      ctx.updateEvent('properties.kochava_device_id', kochava_device_id)
      user?.traits({
        kochava_device_id,
      })
      return ctx
    },
    identify: async (ctx) => {
      const kochava = await getKochavaInstance(appGuid)
      const kochava_device_id = kochava.getDeviceId()
      ctx.updateEvent('traits.kochava_device_id', kochava_device_id)
      user?.traits({
        kochava_device_id,
      })
      return ctx
    },
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function KochavaPlugin({
  appGuid,
}: {
  appGuid: string | undefined
}): Plugin & { instance: Kochava | null; reset?: () => void } {
  try {
    let ready = false
    const kochavaAppGuid = appGuid
    if (!kochavaAppGuid) {
      throw new Error('Kochava app GUID is not set')
    }
    let kochava: Kochava | null = null

    return {
      name: `kochava-${kochavaAppGuid}`,
      version: '0.1.0',
      type: 'destination',
      get instance() {
        return kochava
      },
      /**
       * Has this Custom Facebook Pixel library been loaded yet?
       */
      isLoaded(): boolean {
        return !!(typeof window !== 'undefined' && window.kochava && ready)
      },
      /**
       * Initialize this Custom Facebook Pixel.
       *
       * @param {Facade} page
       */
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      async load(_ctx, _instance) {
        try {
          if (!kochava) {
            kochava = await setupKochavaInstance(kochavaAppGuid)
            await _instance.register(KochavaEnrichmentPlugin({ appGuid: kochavaAppGuid }))
          }
        } catch (error) {
          logger.error(`Kochava plugin failed to load`, undefined, error)
          throw error
        } finally {
          ready = true
        }
      },

      async ready() {
        return ready
      },

      async page(ctx) {
        if (!kochava) return ctx
        const track = new Track(ctx.event)
        setTimeout(() => {
          kochava?.sendPageEvent?.(track.event(), track.properties())
        }, 1000) // delay to allow segment to record any page view from paid media
        return ctx
      },

      async track(ctx) {
        if (!kochava) return ctx
        const track = new Track(ctx.event)

        if (track.event() === 'Order Completed' && track.properties() && track.properties().funnel === 'theatrical') {
          kochava.sendEvent('Order Completed - theatrical', track.properties())
          return ctx
        }

        if (track.event() === 'Order Completed' && track.properties() && track.properties().funnel === 'guild') {
          kochava.sendEvent('Purchase', track.properties())
          return ctx
        }
        return ctx
      },
      async identify(ctx) {
        if (!kochava) return ctx

        const identify = new Identify(ctx.event)

        const identityLinkTraits = Object.fromEntries(
          Object.entries({
            anonymous_id: identify.anonymousId(),
            user_id: identify.userId(),
            email_hash: hashPII(identify.email()),
            phone_hash: hashPII(identify.traits()?.phone),
          }).filter(([, value]) => value !== undefined),
        )

        for (const [key, value] of Object.entries(identityLinkTraits)) {
          if (typeof value === 'string' && value.length > 0) {
            kochava.registerIdentityLink(key, value)
          }
        }

        return ctx
      },
      reset() {
        if (!kochava) return
        kochava.shutdown(true)
        // re setup kochava instance after shutdown
        void setupKochavaInstance(kochavaAppGuid)
      },
    }
  } catch (error) {
    logger.error(`Kochava plugin failed to load`, undefined, error)
    return {
      name: 'kochava',
      version: '0.1.0',
      type: 'destination',
      instance: null,
      isLoaded: () => false,
      load: () => Promise.resolve(),
    }
  }
}

function hashPII(value: unknown) {
  if (!value) return undefined
  if (typeof value === 'string') {
    return sha256(value.trim().toLowerCase())
  }
  return undefined
}
